import React from "react";
import { Link } from "react-router-dom";

/**
 * Shared framing for the three SONMI visual studies.
 *
 * Deliberately not the ProtectedLayout. Studies are:
 *   - hidden from all navigation,
 *   - not connected to backend,
 *   - annotated as *studies*, not product,
 *   - safely removable without touching the preserved product.
 *
 * A slim study strip appears at the top so a reviewer can move between
 * the three states. A study-notes block appears at the bottom,
 * clearly separated from the proposed interface above it.
 */

export function StudyStrip({ current }) {
  const stops = [
    { to: "/sonmi/study/shell",          label: "1 · Shell" },
    { to: "/sonmi/study/people-vessel",  label: "2 · People Vessel" },
    { to: "/sonmi/study/expanded",       label: "3 · Expanded" },
  ];
  return (
    <div
      data-testid="sonmi-study-strip"
      className="w-full border-b border-stone-100 bg-white/60 backdrop-blur-[1px]"
    >
      <div className="max-w-6xl mx-auto px-8 py-3 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-stone-400">
          SONMI · visual study · v0.1 · placeholder content
        </div>
        <div className="flex items-center gap-4">
          {stops.map((s) => (
            <Link
              key={s.to}
              to={s.to}
              data-testid={`study-nav-${s.label.split(" · ")[0]}`}
              className={`text-[11px] tracking-wide ${
                current === s.to
                  ? "text-stone-900"
                  : "text-stone-400 hover:text-stone-700"
              }`}
            >
              {s.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function StudyNotes({ testing, understandFirst, withheld, throughAttention, principles }) {
  return (
    <div
      data-testid="study-notes"
      className="max-w-3xl mx-auto mt-24 mb-16 px-8 pt-10 border-t border-stone-100 text-stone-500"
    >
      <div className="text-[10px] uppercase tracking-[0.18em] text-stone-400 mb-6">
        Study notes · not part of the proposed interface
      </div>
      <div className="text-sm font-light leading-relaxed space-y-5">
        <div>
          <span className="text-stone-800">Testing.</span>{" "}
          {testing}
        </div>
        <div>
          <span className="text-stone-800">What the steward is meant to understand first.</span>{" "}
          {understandFirst}
        </div>
        <div>
          <span className="text-stone-800">What has been deliberately withheld.</span>{" "}
          {withheld}
        </div>
        <div>
          <span className="text-stone-800">What is available through further attention.</span>{" "}
          {throughAttention}
        </div>
        <div>
          <span className="text-stone-800">Principles being tested.</span>{" "}
          {principles}
        </div>
      </div>
    </div>
  );
}

/**
 * A very small, quiet mark for SONMI's ambient presence.
 * Not a companion avatar, not a chat launcher. Only a hint that
 * she is here.
 */
export function SonmiMark({ size = 10 }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block rounded-full bg-stone-300"
      style={{ width: size, height: size }}
    />
  );
}

/**
 * A person's presence in the People Vessel. Not a card. Not a row.
 * A quiet mark: a soft circular initial, a name, an optional single
 * line of Phase-1 style noticing beneath.
 */
export function Presence({ initials, name, note, size = 44, tone = "quiet" }) {
  const bg = tone === "warm" ? "bg-stone-100" : "bg-stone-50";
  return (
    <div className="flex flex-col items-center text-center" data-testid={`presence-${name.replace(/\s+/g, "-").toLowerCase()}`}>
      <div
        className={`${bg} rounded-full flex items-center justify-center text-stone-500 border border-stone-100`}
        style={{ width: size, height: size, fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: size * 0.42 }}
      >
        {initials}
      </div>
      <div className="mt-3 text-[13px] text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
        {name}
      </div>
      {note && (
        <div className="mt-1 text-[11px] text-stone-400 italic max-w-[140px] leading-snug">
          {note}
        </div>
      )}
    </div>
  );
}
